import { useCallback, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useStreamStore } from "@/lib/streamStore";
import { startStream, stopStream, setQueryClient } from "./agentStreamManager";

export interface TraceEvent {
  ts: string;
  kind: string;
  title: string;
  data: Record<string, unknown>;
}

export interface StreamState {
  isStreaming: boolean;
  traceEvents: TraceEvent[];
  finalText: string | null;
  error: string | null;
}

const EMPTY_STREAM_STATE: StreamState = {
  isStreaming: false,
  traceEvents: [],
  finalText: null,
  error: null,
};

export function useAgentStream(sessionId: string | null) {
  const queryClient = useQueryClient();

  // Set the global query client reference for the stream manager
  useEffect(() => {
    setQueryClient(queryClient);
  }, [queryClient]);

  // Subscribe to stream state changes for this specific session
  // Use selector to only re-render when this session's state changes
  const streamState = useStreamStore((state) => {
    if (!sessionId) {
      return EMPTY_STREAM_STATE;
    }
    return state.streamStateBySession[sessionId] || EMPTY_STREAM_STATE;
  });

  const pendingUserMessage = useStreamStore((state) => {
    if (!sessionId) {
      return null;
    }
    return state.pendingUserMessageBySession[sessionId] || null;
  });

  const streamMessage = useCallback(
    async (text: string, skillCommand?: string, model?: string, options?: { webSearchEnabled?: boolean }) => {
      if (!sessionId) {
        throw new Error("Session ID not set");
      }

      // Check if this session is already streaming
      if (streamState.isStreaming) {
        console.warn(`Session ${sessionId} is already streaming, blocking new request`);
        throw new Error("A request is already in progress for this session");
      }

      await startStream({
        sessionId,
        text,
        skillCommand,
        model,
        webSearchEnabled: options?.webSearchEnabled ?? false,
      });
    },
    [sessionId, streamState.isStreaming]
  );

  const stopStreaming = useCallback(() => {
    if (sessionId) {
      stopStream(sessionId);
    }
  }, [sessionId]);

  const reset = useCallback(() => {
    if (sessionId) {
      stopStream(sessionId);
      useStreamStore.getState().resetStreamState(sessionId);
    }
  }, [sessionId]);

  return {
    isStreaming: streamState.isStreaming,
    traceEvents: streamState.traceEvents,
    finalText: streamState.finalText,
    error: streamState.error,
    pendingUserMessage,
    streamMessage,
    stopStreaming,
    reset,
  };
}
